import React from 'react';
import { Button } from '../../Components/MUIs';
import Canvas from '../../Components/Canvas/Canvas';
import PinchZoom from '../../Components/Ui/PinchZoom';

// 회의 안건 카드
function AgendaCard({ index, item }) {
  return (
    <details className="group rounded-xl border border-slate-200 bg-slate-50 open:border-[#2CA900] open:bg-white">
      <summary className="flex cursor-pointer list-none items-start gap-3 p-3 [&::-webkit-details-marker]:hidden">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#2CA900] text-xs font-bold text-white">
          {index + 1}
        </span>
        <span className="flex-1 text-sm leading-snug font-semibold text-slate-800">
          {item.agendaName}
        </span>
        <span className="mt-0.5 shrink-0 text-xs text-slate-400 transition group-open:rotate-180">
          ▼
        </span>
      </summary>
      <div className="space-y-2 border-t border-slate-100 px-3 pt-2 pb-3">
        <div className="rounded-lg bg-white p-3">
          <p className="text-xs font-semibold text-slate-500">의결사항</p>
          <p className="mt-1 text-sm leading-relaxed whitespace-pre-wrap text-slate-700">
            {item.resultDiscussion || '-'}
          </p>
        </div>
        {(item.person || item.processingDeadline) && (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {item.person ? (
              <div className="rounded-lg bg-white p-3">
                <p className="text-xs font-semibold text-slate-500">
                  담당부서/담당자
                </p>
                <p className="mt-1 text-sm text-slate-700">{item.person}</p>
              </div>
            ) : null}
            {item.processingDeadline ? (
              <div className="rounded-lg bg-white p-3">
                <p className="text-xs font-semibold text-slate-500">처리기한</p>
                <p className="mt-1 text-sm text-slate-700">
                  {item.processingDeadline}
                </p>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </details>
  );
}

// 참석자 그룹
function AttendeeGroup({ title, attendees, emptyMessage }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
      <p className="text-xs font-semibold text-slate-600">
        {title}{' '}
        <span className="font-normal text-slate-400">
          ({attendees.length}명)
        </span>
      </p>
      {attendees.length > 0 ? (
        <ul className="mt-3 flex flex-col gap-2.5">
          {attendees.map((person, idx) => (
            <li
              key={person.id || `${person.name}-${idx}`}
              className="flex items-center justify-between gap-2 rounded-lg bg-white px-3 py-2.5 text-sm"
            >
              <span className="font-medium text-slate-800">{person.name}</span>
              {(person.depNm || person.rank) && (
                <span className="truncate text-xs text-slate-500">
                  {[person.depNm, person.rank].filter(Boolean).join(' · ')}
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-xs text-slate-400">{emptyMessage}</p>
      )}
    </div>
  );
}

export default function M_EXT_SIGN_BASIC({
  inputs = {},
  participants = [],
  meetingAgenda = [],
  image = '',
  isCommitteeType = false,
  consentChecked,
  setConsentChecked,
  signatureCanvas,
  signInputs,
  setSignInputs,
  signRegistered = false,
  isEditingSign = false,
  onClear,
  onConfirm,
}) {
  const signLocked = signRegistered && !isEditingSign;

  const gubun0Label = isCommitteeType ? '도급사' : '사용자위원';
  const gubun1Label = isCommitteeType ? '수급사' : '근로자위원';
  const gubun0Attendees = participants.filter((p) => p.gubun === '0');
  const gubun1Attendees = participants.filter((p) => p.gubun === '1');

  return (
    <div className="flex min-h-[100dvh] flex-col bg-slate-50">
      <header className="border-b border-slate-200 bg-white px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <h1 className="text-lg font-bold text-slate-900">외부 참여자 서명</h1>
        <p className="mt-0.5 text-xs text-slate-500">
          회의 내용을 확인한 뒤 전자서명을 진행해 주세요.
        </p>
      </header>

      <div className="flex-1 overflow-y-auto p-4 pb-6">
        <section className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold tracking-wide text-[#2CA900] uppercase">
            회의정보
          </p>
          <h2 className="mt-2 text-base leading-snug font-bold text-slate-900">
            {inputs.proceedingsTitle || '회의 제목 없음'}
          </h2>
          <dl className="mt-3 space-y-2 text-sm">
            <div className="flex gap-2">
              <dt className="w-16 shrink-0 text-slate-500">일시</dt>
              <dd className="text-slate-800">
                <p>{inputs.formattedDate || '-'}</p>
                {inputs.formattedTime ? (
                  <p className="mt-0.5 text-xs text-slate-600">
                    {inputs.formattedTime}
                  </p>
                ) : null}
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="w-16 shrink-0 text-slate-500">장소</dt>
              <dd className="text-slate-800">
                {inputs.proceedingsLocation || '회의 장소 없음'}
              </dd>
            </div>
          </dl>
        </section>

        {participants.length > 0 ? (
          <section className="mt-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-baseline justify-between">
              <p className="text-sm font-semibold text-slate-800">참석자</p>
              <p className="text-xs text-slate-500">
                총원 {participants.length}명
              </p>
            </div>
            <div className="space-y-2">
              <AttendeeGroup
                title={gubun0Label}
                attendees={gubun0Attendees}
                emptyMessage={`${gubun0Label} 참석자 없음`}
              />
              <AttendeeGroup
                title={gubun1Label}
                attendees={gubun1Attendees}
                emptyMessage={`${gubun1Label} 참석자 없음`}
              />
            </div>
          </section>
        ) : null}

        <section className="mt-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-800">회의 안건</p>
            <span className="rounded-full bg-[#2CA900] px-2.5 py-0.5 text-xs font-bold text-white">
              {meetingAgenda.length}건
            </span>
          </div>
          {meetingAgenda.length > 0 ? (
            <div className="space-y-2">
              {meetingAgenda.map((item, index) => (
                <AgendaCard key={index} index={index} item={item} />
              ))}
            </div>
          ) : (
            <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 py-8 text-center text-sm text-slate-400">
              등록된 안건이 없습니다.
            </p>
          )}
        </section>

        <section className="mt-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
          <p className="text-sm font-semibold text-slate-800">진행 사진</p>
          <div className="mt-3 h-64 overflow-hidden rounded-xl border border-dashed border-slate-200 bg-slate-50">
            {image ? (
              <PinchZoom
                wrapperStyle={{
                  width: '100%',
                  height: '100%',
                }}
                componentWrapperStyle={{
                  width: '100%',
                  height: '100%',
                }}
                componentContentStyle={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                contents={
                  <img
                    src={image}
                    alt="회의 진행 사진"
                    className="max-h-full max-w-full object-contain"
                  />
                }
              />
            ) : (
              <p className="grid h-full place-items-center text-sm text-slate-400">
                등록된 이미지가 없습니다.
              </p>
            )}
          </div>
        </section>

        <section className="mt-4 space-y-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
          <p className="text-sm font-semibold text-slate-800">참석자 정보</p>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-700">
              성명
            </label>
            <input
              className="h-11 w-full cursor-not-allowed rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-600 ring-0 outline-none"
              placeholder="실명을 입력해 주세요"
              value={inputs.name}
              readOnly
              disabled
              maxLength={50}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-700">
              소속
            </label>
            <input
              className="h-11 w-full cursor-not-allowed rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-600 ring-0 outline-none"
              placeholder="회사 또는 조직명"
              value={inputs.depNm}
              readOnly
              disabled
              maxLength={100}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-700">
              연락처
            </label>
            <input
              className="h-11 w-full cursor-not-allowed rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-600 ring-0 outline-none"
              placeholder="01012345678"
              value={inputs.tel}
              readOnly
              disabled
              maxLength={11}
              inputMode="numeric"
            />
          </div>
        </section>

        {signRegistered && (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm font-semibold text-amber-900">
              서명이 완료된 회의입니다
            </p>
            <p className="mt-1 text-xs leading-relaxed text-amber-800">
              {isEditingSign
                ? '새 서명을 입력한 뒤 서명 제출을 눌러 주세요.'
                : '등록된 서명을 확인할 수 있습니다. 변경하려면 서명 지우기를 눌러 주세요.'}
            </p>
          </div>
        )}

        <section className="mt-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
          <p className="text-sm font-semibold text-slate-800">서명</p>
          <p className="mt-1 text-xs leading-relaxed text-slate-600">
            {signLocked
              ? '등록된 서명입니다. 변경하려면 서명 지우기를 눌러 주세요.'
              : '아래 영역에 손가락 또는 스타일러스로 서명해 주세요. 제출 시 전자서명으로 간주됩니다.'}
          </p>
          <div className="mt-3">
            <Canvas
              signatureCanvas={signatureCanvas}
              userId={inputs.name}
              imageUrl={signInputs.mapUrl}
              disabled={signLocked}
              onEnd={() => {
                if (signLocked || !signatureCanvas.current) return;
                const signatureDataUrl = signatureCanvas.current.toDataURL();
                const dataURL = signatureCanvas.current.toDataURL('image/png');
                const decodedData = atob(dataURL.split(',')[1]);
                const byteNumbers = new Array(decodedData.length);

                for (let i = 0; i < decodedData.length; i++) {
                  byteNumbers[i] = decodedData.charCodeAt(i);
                }

                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], {
                  type: 'image/png',
                });
                const file = new File(
                  [blob],
                  `${new Date()
                    .toISOString()
                    .replace(/T/, '')
                    .replace(/\..+/, '')
                    .replace(/-/g, '')
                    .replace(/:/g, '')}.png`,
                  {
                    type: 'image/png',
                  }
                );

                setSignInputs({
                  mapUrl: signatureDataUrl,
                  mapFiles: file,
                });
              }}
            />
          </div>
          <div className="mt-3">
            <Button
              variant="outlined"
              className="h-9 w-full rounded-full"
              name="서명 지우기"
              onClick={onClear}
            />
          </div>
        </section>

        <section className="mt-4 rounded-2xl border border-slate-100 bg-white p-3 shadow-sm">
          <label className="flex cursor-pointer items-start gap-2">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#2CA900] focus:ring-[#2CA900]"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
            />
            <span className="text-xs leading-snug text-slate-700">
              [필수] 본인은 위 회의 내용을 확인하였으며, 서명 정보 수집·이용 및
              전자서명에 동의합니다.
            </span>
          </label>
        </section>
      </div>

      <div className="border-t border-slate-200 bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <Button
          className="h-11 w-full rounded-full bg-[#2EBD36] text-white disabled:bg-slate-300 disabled:text-slate-500"
          name={signLocked ? '서명 완료' : '서명 제출'}
          onClick={onConfirm}
          disabled={signLocked}
        />
      </div>
    </div>
  );
}
